import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { Bookmark, Heart, Library, Newspaper, Trash2, User } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { profileService } from '@/api';
import { Button, FeedSkeleton, useToast } from '@/components/ui';
import { ContentCard } from '@/features/feed/components/ContentCard';
import { EmptyFeed } from '@/features/feed/components/EmptyFeed';
import { FeedHeader } from '@/features/feed/components/FeedHeader';
import { useFeed } from '@/features/feed/hooks/useFeed';
import { usePublicFeed } from '@/features/feed/hooks/usePublicFeed';
import {
  FROM_PUBLIC_FEED_KEY,
  PublicContentCard,
  SignupWallModal,
  useSignupWall,
} from '@/features/public-feed';
import { useLibrary } from '@/features/saved/hooks/useLibrary';
import { useAuth } from '@/providers';
import { isAdminRole } from '@/types';

// Number of visible cards for anonymous users
const VISIBLE_CARDS = 2;

type ViewType = 'feed' | 'library';
type LibraryTab = 'saved' | 'liked';

interface IncomingNavigationState {
  view?: ViewType;
  tab?: LibraryTab;
}

function ViewToggle({
  activeView,
  onViewChange,
}: {
  activeView: ViewType;
  onViewChange: (view: ViewType) => void;
}) {
  return (
    <div className="relative flex items-center gap-1 p-1 bg-[#F4F2F2] rounded-xl">
      {/* Feed button */}
      <button
        type="button"
        onClick={() => onViewChange('feed')}
        className="relative flex items-center justify-center gap-2 w-[120px] py-2 rounded-lg transition-colors duration-200 cursor-pointer"
      >
        {activeView === 'feed' && (
          <motion.div
            layoutId="viewToggleIndicator"
            className="absolute inset-0 bg-[#2E2A23] rounded-lg"
            transition={{
              type: 'spring',
              stiffness: 500,
              damping: 35,
            }}
          />
        )}
        <Newspaper
          className={`relative z-10 w-4 h-4 transition-colors duration-200 ${
            activeView === 'feed' ? 'text-[#F4F2F2]' : 'text-[#888185]'
          }`}
        />
        <span
          className={`relative z-10 text-sm font-medium transition-colors duration-200 ${
            activeView === 'feed' ? 'text-[#F4F2F2]' : 'text-[#888185]'
          }`}
        >
          Feed
        </span>
      </button>

      {/* Library button */}
      <button
        type="button"
        onClick={() => onViewChange('library')}
        className="relative flex items-center justify-center gap-2 w-[120px] py-2 rounded-lg transition-colors duration-200 cursor-pointer"
      >
        {activeView === 'library' && (
          <motion.div
            layoutId="viewToggleIndicator"
            className="absolute inset-0 bg-[#2E2A23] rounded-lg"
            transition={{
              type: 'spring',
              stiffness: 500,
              damping: 35,
            }}
          />
        )}
        <Library
          className={`relative z-10 w-4 h-4 transition-colors duration-200 ${
            activeView === 'library' ? 'text-[#F4F2F2]' : 'text-[#888185]'
          }`}
        />
        <span
          className={`relative z-10 text-sm font-medium transition-colors duration-200 ${
            activeView === 'library' ? 'text-[#F4F2F2]' : 'text-[#888185]'
          }`}
        >
          Biblioteca
        </span>
      </button>
    </div>
  );
}

function LibraryTabs({
  activeTab,
  onTabChange,
  savedCount,
  likedCount,
}: {
  activeTab: LibraryTab;
  onTabChange: (tab: LibraryTab) => void;
  savedCount: number;
  likedCount: number;
}) {
  return (
    <div className="flex items-center gap-2 mb-6">
      <button
        type="button"
        onClick={() => onTabChange('saved')}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-all cursor-pointer ${
          activeTab === 'saved'
            ? 'bg-[#E94E1B] text-white'
            : 'bg-[#F4F2F2] text-[#888185] hover:bg-[#CAC7C7]'
        }`}
      >
        <Bookmark className="w-3.5 h-3.5" />
        <span>Salvos</span>
        <span className={`text-xs ${activeTab === 'saved' ? 'text-white/70' : 'text-[#888185]'}`}>
          {savedCount}
        </span>
      </button>
      <button
        type="button"
        onClick={() => onTabChange('liked')}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-all cursor-pointer ${
          activeTab === 'liked'
            ? 'bg-[#E94E1B] text-white'
            : 'bg-[#F4F2F2] text-[#888185] hover:bg-[#CAC7C7]'
        }`}
      >
        <Heart className="w-3.5 h-3.5" />
        <span>Curtidos</span>
        <span className={`text-xs ${activeTab === 'liked' ? 'text-white/70' : 'text-[#888185]'}`}>
          {likedCount}
        </span>
      </button>
    </div>
  );
}

function LibraryCard({
  item,
  type,
  onRemove,
}: {
  item: {
    content: { id: string; title: string; summary: string; author: string; publishedAt: string };
  };
  type: LibraryTab;
  onRemove: (contentId: string) => void;
}) {
  const navigationState = { origin: 'library' as const, libraryTab: type };
  const formattedDate = new Date(item.content.publishedAt).toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'short',
  });

  return (
    <article className="group bg-white rounded-lg border border-[#CAC7C7] hover:border-[#E94E1B]/30 hover:shadow-sm transition-all duration-200">
      <Link to={`/content/${item.content.id}`} state={navigationState} className="block p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h3 className="font-serif text-base text-[#2E2A23] line-clamp-2 mb-2 group-hover:text-[#E94E1B] transition-colors">
              {item.content.title}
            </h3>
            <p className="text-sm text-[#888185] line-clamp-2 mb-3">{item.content.summary}</p>
            <div className="flex items-center gap-2 text-xs text-[#888185]">
              <span>{item.content.author}</span>
              <span className="text-[#CAC7C7]">·</span>
              <span>{formattedDate}</span>
            </div>
          </div>

          <div className="flex-shrink-0">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                type === 'saved' ? 'bg-[#FDEAE3]' : 'bg-[#FEE2E2]'
              }`}
            >
              {type === 'saved' ? (
                <Bookmark className="w-5 h-5 text-[#E94E1B] fill-[#E94E1B]" />
              ) : (
                <Heart className="w-5 h-5 text-[#E94E1B] fill-[#E94E1B]" />
              )}
            </div>
          </div>
        </div>
      </Link>

      <div className="px-4 pb-3 flex justify-end border-t border-[#F4F2F2]">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onRemove(item.content.id);
          }}
          className="flex items-center gap-1.5 px-2 py-1.5 text-xs text-[#888185] hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 cursor-pointer"
        >
          <Trash2 className="w-3.5 h-3.5" />
          <span>Remover</span>
        </button>
      </div>
    </article>
  );
}

function EmptyLibrary({ type }: { type: LibraryTab }) {
  const Icon = type === 'saved' ? Bookmark : Heart;
  const title = type === 'saved' ? 'Nenhum item salvo' : 'Nenhum item curtido';
  const description =
    type === 'saved'
      ? 'Salve artigos para ler mais tarde clicando no ícone de marcador.'
      : 'Curta artigos que você achou úteis clicando no ícone de coração.';

  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 bg-[#F4F2F2] rounded-full flex items-center justify-center mb-6">
        <Icon className="w-8 h-8 text-[#E94E1B]" />
      </div>
      <h3 className="font-serif text-xl text-[#2E2A23] mb-2">{title}</h3>
      <p className="text-[#888185] max-w-sm">{description}</p>
    </div>
  );
}

function LibrarySkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="bg-white rounded-lg border border-[#CAC7C7] p-4 animate-pulse">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="h-5 w-3/4 bg-[#CAC7C7] rounded mb-3" />
              <div className="h-4 w-full bg-[#CAC7C7] rounded mb-2" />
              <div className="h-4 w-2/3 bg-[#CAC7C7] rounded mb-3" />
              <div className="flex gap-2">
                <div className="h-3 w-20 bg-[#CAC7C7] rounded" />
                <div className="h-3 w-16 bg-[#CAC7C7] rounded" />
              </div>
            </div>
            <div className="w-10 h-10 bg-[#CAC7C7] rounded-lg" />
          </div>
        </div>
      ))}
    </div>
  );
}

// Continue overlay for anonymous users (shown over blurred cards)
function ContinueOverlay() {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="relative w-full py-16 flex items-center justify-center"
    >
      <div className="text-center">
        <div className="mb-6 flex justify-center">
          <div className="w-12 h-12 rounded-full bg-[#E94E1B] flex items-center justify-center">
            <User size={24} className="text-white" strokeWidth={2} />
          </div>
        </div>
        <h3 className="font-serif text-[24px] font-semibold text-[#2E2A23] mb-3">
          {t('publicFeed.continueOverlay.title', 'Continue lendo')}
        </h3>
        <p className="font-sans text-[15px] text-[#888185] mb-6 max-w-md mx-auto">
          {t(
            'publicFeed.continueOverlay.description',
            'Faça login ou crie uma conta para acessar mais histórias selecionadas para você.',
          )}
        </p>
        <div className="flex gap-3 justify-center">
          <Link to="/login?mode=signup">
            <Button size="lg" className="rounded-lg shadow-sm">
              {t('publicFeed.continueOverlay.signup', 'Criar conta')}
            </Button>
          </Link>
          <Link to="/login">
            <Button variant="outline" size="lg" className="rounded-lg">
              {t('publicFeed.continueOverlay.login', 'Entrar')}
            </Button>
          </Link>
        </div>
      </div>
    </motion.div>
  );
}

export function FeedPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState<ViewType>('feed');
  const [libraryTab, setLibraryTab] = useState<LibraryTab>('saved');

  // Signup wall for anonymous users (2 second delay, 10 second cooldown)
  const { isOpen: isSignupWallOpen, close: closeSignupWall } = useSignupWall(2000);
  const [showModalOnInteraction, setShowModalOnInteraction] = useState(false);

  // Get profile data (includes user name from database) - only for authenticated users
  const {
    data: profile,
    isLoading: profileLoading,
    isFetched: profileFetched,
  } = useQuery({
    queryKey: ['profile'],
    queryFn: () => profileService.getProfile(),
    enabled: isAuthenticated,
  });

  // Redirect to onboarding if authenticated user has no profile
  useEffect(() => {
    if (isAuthenticated && profileFetched && !profile) {
      navigate('/onboarding', { replace: true });
    }
  }, [isAuthenticated, profileFetched, profile, navigate]);

  // Show welcome toast if user came from public feed (only for authenticated users)
  useEffect(() => {
    if (!isAuthenticated) return;
    const fromPublicFeed = sessionStorage.getItem(FROM_PUBLIC_FEED_KEY);
    if (fromPublicFeed) {
      sessionStorage.removeItem(FROM_PUBLIC_FEED_KEY);
      toast(
        t('feed.welcomeMessage', 'Bem-vindo(a) à Newsletter! Seu feed personalizado está pronto.'),
        'success',
      );
    }
  }, [toast, t, isAuthenticated]);

  // Restore view and tab from navigation state (coming back from content detail)
  useEffect(() => {
    const navState = location.state as IncomingNavigationState | undefined;
    if (navState?.view) {
      setActiveView(navState.view);
    }
    if (navState?.tab) {
      setLibraryTab(navState.tab);
    }
  }, [location.state]);

  // Authenticated feed data
  const { items, hasMore, isLoading, isError, toggleLike, toggleSave, dismiss, feedback, mute } =
    useFeed();

  // Public feed data (for anonymous users)
  const { items: publicItems, isLoading: publicLoading, isError: publicError } = usePublicFeed();

  const { savedItems, likedItems, isLoading: libraryLoading, unsave, unlike } = useLibrary();

  const handleMore = useCallback(
    (contentId: string, action: 'feedback' | 'muteAuthor' | 'muteSource') => {
      const item = items.find((i) => i.content.id === contentId);
      if (!item) return;

      switch (action) {
        case 'feedback':
          feedback(contentId);
          break;
        case 'muteAuthor':
          mute({ type: 'author', value: item.content.author });
          break;
        case 'muteSource':
          mute({ type: 'source', value: item.content.source });
          break;
      }
    },
    [items, feedback, mute],
  );

  const handleInteractionAttempt = useCallback(() => {
    setShowModalOnInteraction(true);
  }, []);

  const closeInteractionModal = useCallback(() => {
    setShowModalOnInteraction(false);
  }, []);

  const libraryItems = libraryTab === 'saved' ? savedItems : likedItems;
  const handleLibraryRemove = libraryTab === 'saved' ? unsave : unlike;

  // Use name from profile (database) with fallback to email prefix
  const profileData = profile as Record<string, unknown> | null;
  const userName = isAuthenticated
    ? (profileData?.name as string) || user?.email?.split('@')[0]
    : undefined;
  const isAdmin = isAdminRole(profileData?.role as string | undefined);

  // Determine which feed data to use
  const feedItems = isAuthenticated ? items : publicItems;
  // Include profile loading in the overall loading state
  const feedLoading = isAuthenticated ? isLoading || profileLoading : publicLoading;
  const feedError = isAuthenticated ? isError : publicError;

  return (
    <div className="min-h-screen bg-[#F4F2F2]">
      {/* Header - different for authenticated vs anonymous */}
      {isAuthenticated ? (
        <FeedHeader userName={userName} isAdmin={isAdmin} />
      ) : (
        <header className="sticky top-0 z-40 bg-white border-b border-[#CAC7C7]">
          <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link
              to="/"
              className="font-serif text-xl text-[#2E2A23] hover:text-[#E94E1B] transition-colors"
            >
              Newsletter
            </Link>
            <Link to="/login">
              <Button variant="outline" size="sm" className="rounded-full">
                {t('auth.signIn', 'Entrar')}
              </Button>
            </Link>
          </div>
        </header>
      )}

      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* View Toggle - only for authenticated users */}
        {isAuthenticated && (
          <div className="flex justify-center mb-6">
            <ViewToggle activeView={activeView} onViewChange={setActiveView} />
          </div>
        )}

        {/* Page header for anonymous users */}
        {!isAuthenticated && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="font-serif text-3xl md:text-4xl text-[#2E2A23] mb-2">
              {t('publicFeed.title', 'Conteúdo em destaque')}
            </h1>
            <p className="text-[#888185]">
              {t('publicFeed.subtitle', 'Veja uma prévia do que a Newsletter tem para você')}
            </p>
          </motion.div>
        )}

        {/* Feed View */}
        {activeView === 'feed' &&
          (feedLoading ? (
            <FeedSkeleton />
          ) : feedError ? (
            <EmptyFeed variant="empty" />
          ) : feedItems.length === 0 ? (
            <EmptyFeed variant="slow" />
          ) : (
            <>
              {isAuthenticated ? (
                // Authenticated: full ContentCard with interactions
                <div className="space-y-4">
                  <AnimatePresence mode="popLayout">
                    {items.map((item) => (
                      <ContentCard
                        key={item.content.id}
                        item={item}
                        onLike={toggleLike}
                        onSave={toggleSave}
                        onDismiss={dismiss}
                        onMore={handleMore}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              ) : (
                // Anonymous: First VISIBLE_CARDS visible, rest blurred with overlay
                <>
                  {/* Visible cards */}
                  <div className="space-y-4">
                    <AnimatePresence mode="popLayout">
                      {publicItems.slice(0, VISIBLE_CARDS).map((item) => (
                        <PublicContentCard
                          key={item.content.id}
                          item={item}
                          isBlurred={false}
                          onInteractionAttempt={handleInteractionAttempt}
                        />
                      ))}
                    </AnimatePresence>
                  </div>

                  {/* Blurred cards section with overlay */}
                  {publicItems.length > VISIBLE_CARDS && (
                    <div className="relative mt-4">
                      <div className="blur-sm pointer-events-none select-none space-y-4">
                        {publicItems.slice(VISIBLE_CARDS).map((item) => (
                          <PublicContentCard
                            key={item.content.id}
                            item={item}
                            isBlurred={true}
                            onInteractionAttempt={handleInteractionAttempt}
                          />
                        ))}
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#F4F2F2]/60 to-[#F4F2F2] pointer-events-none" />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-auto">
                        <ContinueOverlay />
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* End of feed message - only for authenticated */}
              {isAuthenticated && !hasMore && items.length > 0 && <EmptyFeed variant="end" />}
            </>
          ))}

        {/* Library View - only for authenticated users */}
        {isAuthenticated && activeView === 'library' && (
          <>
            <LibraryTabs
              activeTab={libraryTab}
              onTabChange={setLibraryTab}
              savedCount={savedItems.length}
              likedCount={likedItems.length}
            />

            {libraryLoading ? (
              <LibrarySkeleton />
            ) : libraryItems.length === 0 ? (
              <EmptyLibrary type={libraryTab} />
            ) : (
              <div className="space-y-3">
                {libraryItems.map((item) => (
                  <LibraryCard
                    key={`${libraryTab}-${item.content.id}`}
                    item={item}
                    type={libraryTab}
                    onRemove={handleLibraryRemove}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* Signup wall modals - only for anonymous users */}
      {!isAuthenticated && (
        <>
          <SignupWallModal isOpen={isSignupWallOpen} onClose={closeSignupWall} />
          <SignupWallModal isOpen={showModalOnInteraction} onClose={closeInteractionModal} />
        </>
      )}
    </div>
  );
}
